import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  IFiltroJurisprudencia,
  IJurisprudencia,
  ISubfiltros,
  PageJurisprudencia,
} from '../types';
import JurisprudenciaSelecionadaService from '@/views/jurisprudencia-selecionada/service';
import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';
import {
  transformarDadosSubFilter,
  verificarSePossuiItensSelecionadosOuExpandidos,
} from '@/utils/transform';
import { cloneDeep } from '@apollo/client/utilities';

export const ConsultaJurisprudenciaSelecionadaStore = defineStore(
  'consultaJurisprudenciaSelecionadaStore',
  () => {
    const pageJurisprudencia = ref<PageJurisprudencia>({});

    const showFilter = ref(false);

    const pageSubfiltros = ref<ISubfiltros[]>([]);

    const pageSubfiltrosOld = ref<ISubfiltros[] | undefined>(undefined);

    const filtroJurisprudencia = ref<IFiltroJurisprudencia>({
      dePesquisaTermos: null,
      flEmenta: true,
      flInteiroTeor: true,
      ordenacao: 'dtjulgamento desc',
      expandirPesquisaAvancada: false,
    });

    const jurisprudencias = computed(
      () => pageJurisprudencia.value.jurisprudencias || []
    );

    const subFiltrosCategorias = computed(() => {
      if (pageSubfiltros.value) {
        return transformarDadosSubFilter(pageSubfiltros.value);
      }
      return {
        classes: [],
        relatores: [],
        comarcas: [],
        orgaosJulgadores: [],
        revistas: [],
        anos: [],
        prolatores: [],
      };
    });

    const totalHits = computed(() => pageJurisprudencia.value.totalHits || 0);

    const limiteTotalHists = 10000;

    const pagina = ref(1);

    const totalElementos = computed(() => {
      if (totalHits.value > limiteTotalHists) return limiteTotalHists;
      return totalHits.value;
    });

    const jurisprudenciasSelecionadas = ref<IJurisprudencia[]>([]);

    const acordaosJurisprudenciasSelecionadas = computed(
      () =>
        jurisprudenciasSelecionadas.value
          .filter((item) => item.flSegredoJustica === 'N')
          .map((item) => item.cdAcordao) || []
    );

    const codigosJurisprudenciasSelecionadas = computed(
      () =>
        jurisprudenciasSelecionadas.value.map(
          (item) => item.cdJurisprudencia
        ) || []
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params = computed<any>(() => {
      const {
        dePesquisaTermos,
        flEmenta,
        flInteiroTeor,
        dtJulgamentoInicio,
        dtJulgamentoFim,
        dtRegistroInicio,
        dtRegistroFim,
        nuRecurso,
        nuRegistro,
        relator,
        magistradoProlator,
        secao,
        orgaoJulgador,
        comarca,
        classe,
        boletim,
        ordenacao,
      } = filtroJurisprudencia.value;
      return {
        dePesquisaTermos,
        flEmenta,
        flInteiroTeor,
        dtJulgamentoInicio: dtJulgamentoInicio
          ? new Date(dtJulgamentoInicio).getTime()
          : undefined,
        dtJulgamentoFim: dtJulgamentoFim
          ? new Date(dtJulgamentoFim).getTime()
          : undefined,
        dtRegistroInicio: dtRegistroInicio
          ? new Date(dtRegistroInicio).getTime()
          : undefined,
        dtRegistroFim: dtRegistroFim
          ? new Date(dtRegistroFim).getTime()
          : undefined,
        nuRecurso,
        nuRegistro,
        relatorSelected: relator ? relator : [],
        prolatorSelected: magistradoProlator
          ? magistradoProlator
          : magistradoProlator,
        tipoCartorioSelected: secao ? secao : [],
        varaSelected: orgaoJulgador ? orgaoJulgador : [],
        comarcaSelected: comarca ? comarca : [],
        classeSelected: classe ? classe : [],
        tipoBoletimSelected: boletim ? boletim : [],
        numPag: pagina.value - 1,
        ordenacao,
        flPesquisaPlurais: 'N',
        legislativasNormativas: [
          {
            tipoNorma: {},
            nuNorma: '',
            deArtigo: '',
            deParagrafo: '',
            deInciso: '',
            deAlinea: '',
            deNumero: '',
          },
        ],
        subFilter: subFiltrosCategorias.value,
        subFiltrosOld: pageSubfiltrosOld.value || undefined,
      };
    });

    // Actions
    const consultaJurisprudenciaSelecionada = async (
      initialPagination = false
    ) => {
      pageJurisprudencia.value = {};
      try {
        if (initialPagination) {
          pagina.value = 1;
          pageSubfiltros.value = [];
          pageSubfiltrosOld.value = undefined;
          showFilter.value = false;
          jurisprudenciasSelecionadas.value = [];
        }

        const response =
          await JurisprudenciaSelecionadaService.consultarJurisprudenciaSelecionada(
            params.value
          );

        if (response.hasError) {
          const msgErro = response.error?.message;
          SdsSnackbarQueue.error(
            msgErro ? msgErro : 'Não foi possível buscar os registros'
          );
          return;
        }

        pageSubfiltrosOld.value = cloneDeep(pageSubfiltros.value);

        pageJurisprudencia.value = response.content!;

        const subfiltrosAtualizados =
          pageJurisprudencia.value.subfiltros || undefined;

        if (pageSubfiltrosOld.value && pageSubfiltrosOld.value.length > 0) {
          pageSubfiltros.value = verificarSePossuiItensSelecionadosOuExpandidos(
            subfiltrosAtualizados,
            pageSubfiltrosOld.value
          );
        } else {
          pageSubfiltros.value = subfiltrosAtualizados!;
        }
      } catch (error) {
        SdsSnackbarQueue.error('Não foi possível buscar os registros');
      }
    };

    const limparTodosFiltroJurisprudencia = () => {
      pageJurisprudencia.value = {};
      pageSubfiltros.value = [];
      pageSubfiltrosOld.value = undefined;
      filtroJurisprudencia.value = {
        dePesquisaTermos: null,
        flEmenta: true,
        flInteiroTeor: true,
        ordenacao: 'dtjulgamento desc',
      };
    };

    const download = (tipoImpressao: 'A' | 'S' | 'D') => {
      if (tipoImpressao === 'A') {
        acordaosJurisprudenciasSelecionadas.value.forEach((item) =>
          JurisprudenciaSelecionadaService.downloadAcordao(item)
        );
      }

      if (tipoImpressao === 'S') {
        JurisprudenciaSelecionadaService.downloadSimplificado(
          codigosJurisprudenciasSelecionadas.value
        );
      }

      if (tipoImpressao === 'D') {
        JurisprudenciaSelecionadaService.downloadDetalhado(
          codigosJurisprudenciasSelecionadas.value
        );
      }
    };

    return {
      pageJurisprudencia,
      pageSubfiltros,
      pageSubfiltrosOld,
      pagina,
      totalHits,
      jurisprudencias,
      totalElementos,
      filtroJurisprudencia,
      jurisprudenciasSelecionadas,
      subFiltrosCategorias,
      showFilter,
      consultaJurisprudenciaSelecionada,
      limparTodosFiltroJurisprudencia,
      download,
    };
  }
);
